'use client';

import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from './cn';

export type ChipTone =
  | 'neutral'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'success'
  | 'info';
/** Closed data-boundary vocabulary; optional props may separately be undefined. */
export type ChipColor = 'default' | ChipTone;

/** The retained app color vocabulary is normalized once at data boundaries;
 * rendering still uses the atom's one semantic tone recipe, never a theme. */
export function toneForColor(value?: string): ChipTone {
  const tones: Record<string, ChipTone> = {
    default: 'neutral',
    neutral: 'neutral',
    primary: 'primary',
    secondary: 'secondary',
    error: 'error',
    warning: 'warning',
    info: 'info',
    success: 'success',
  };
  return tones[value ?? 'default'] ?? 'neutral';
}

export type ChipSize = 'small' | 'medium';
export type ChipVariant = 'outlined' | 'filled';

export interface UiChipProps extends React.HTMLAttributes<HTMLElement> {
  label: React.ReactNode;
  /** Default 'outlined' (the app wrapper's default). */
  variant?: ChipVariant;
  /** Default 'neutral'. `info` renders as neutral — see the docblock. */
  tone?: ChipTone;
  /** Default 'medium' (MUI: 32px; small: 24px). */
  size?: ChipSize;
  /** 16px pill vs 8px rounded, the app wrapper's `rounded` prop. */
  rounded?: boolean;
  /** Rendered in the icon slot (margins per MUI 6.5.0 metrics). */
  icon?: React.ReactNode;
  onDelete?: (event: React.SyntheticEvent) => void;
  /**
   * Accessible name of the delete button. Defaults to "Delete" (MUI's own
   * label). A list of removable chips — `ui/combobox`'s selections — must
   * override it per chip ("Remove Private wealth"): five buttons all named
   * "Delete" are indistinguishable to a screen reader and to a test.
   */
  deleteLabel?: string;
  /** Default: a plain 22px x (16px at small), sized by the slot's font-size. */
  deleteIcon?: React.ReactNode;
  disabled?: boolean;
  /** role=button + keyboard semantics; the app wrapper maps `clickable` here. */
  interactive?: boolean;
  component?: 'span' | 'button';
  slotProps?: {
    label?: { className?: string; style?: React.CSSProperties };
    icon?: { className?: string; style?: React.CSSProperties };
    delete?: { className?: string; style?: React.CSSProperties };
  };
}

/**
 * The new-system chip: MUI-chip DOM shape and metrics, styled exclusively by
 * Tailwind utilities + the token bridge. This is NOT a reimplementation of
 * every MUI Chip feature — it carries what the app actually uses (Appendix A
 * census): label/icon/delete slots, outlined|filled x 7 tones, small|medium,
 * rounded, disabled, and the clickable/keyboard semantics the wrapper needs.
 *
 * Compatibility hooks — DO NOT RENAME, they are load-bearing selector hooks,
 * not MUI internals: production call sites target chip internals from outside
 * the chip (`ClientRiskRatingReviewSurface.tsx` uses
 * `'& [data-factor-profile] > .MuiChip-root'`) and through this wrapper's own
 * `sx` slot selectors (`team-management-view.tsx`, `screening-table-columns`
 * and `AgentTrajectoryPanel` style `& .MuiChip-label` / `-icon` there), so
 * the root/label/icon/delete elements keep emitting `MuiChip-root` /
 * `MuiChip-label` / `MuiChip-icon` / `MuiChip-deleteIcon` class names as
 * inert hooks — plus the generated variant/colour/size/deletable set MUI's
 * own DOM carried (`MuiChip-filled`, `MuiChip-colorDefault`,
 * `MuiChip-sizeSmall`, …; see `muiHookClasses`).
 * They are plain classes here — no MUI CSS is involved unless a raw MUI Chip
 * renders elsewhere in the app, and where it does, the layer order
 * (@layer components/utilities over @layer mui) keeps our chrome winning.
 *
 * `tone='info'` intentionally renders the neutral recipe: today
 * `getChipSx` treats `color='info'` as `isNeutralOutline`, and the flavour
 * recipes in `src/tailwind.css` keep that behaviour (`[data-chip-tone='info']`
 * is a neutral selector). Not implemented (unreferenced in src): `sx`,
 * `size='large'`, avatars, ripples, and the MUI colour-focus backgrounds.
 *
 * Colour flavours live in `src/tailwind.css` as `[data-chip-*]` recipes so
 * they stay greppable and measurable next to the other component recipes.
 */
/*
 * MUI's default delete glyph — `internal/svg-icons/Cancel.js`, a 24px
 * circle-x — reproduced inline: the ui primitives cannot import from
 * `@mui/*` (lint-enforced), and the bare stroke-x this shipped with was not
 * blessed by the design round. MUI parity (Cancel disc) is the default until
 * the glyph gets a Phase-2 decision (code review round 1, R1-5).
 */
const DefaultDeleteIcon = () => (
  <svg
    aria-hidden="true"
    fill="currentColor"
    height="1em"
    viewBox="0 0 24 24"
    width="1em"
  >
    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
  </svg>
);

/*
 * Non-colour chrome, from the MUI 6.5.0 metrics (Chip.js root + the app's
 * theme overrides): fixed 32/24px heights (pinned — see the size variants),
 * 12px/500 label in BOTH sizes, no transition (theme override
 * transition:none), shadow-xs (wrapper class), and an outline suppressed by
 * default the way MUI chips suppress it — the interactive focus ring below is
 * the one intentional a11y addition (WCAG 2.4.7; MUI chips focus invisibly).
 *
 * NO `cursor` HERE. The chip's cursor is per-state (default / pointer /
 * not-allowed) and is computed once in `cursorClassFor` below, because the
 * base-layer cursor contract cannot resolve it: a chip that is BOTH
 * interactive and disabled carries `role="button"` with no `disabled`
 * attribute for the contract to match. The old blanket `cursor-default` is
 * the affordance the fix removes — it was MUI parity for a NON-clickable
 * chip, but it applied to clickable chips too (including the clickable ones
 * the app wrapper rendered), which is exactly the arrow the operator reported
 * on a chip that is a button.
 */
const chipRootVariants = cva(
  [
    'box-border inline-flex max-w-full items-center justify-center whitespace-nowrap align-middle',
    'shadow-xs outline-none [transition:none]',
  ],
  {
    variants: {
      size: {
        // Heights are the fixed MUI metrics (32px / 24px) rather than rem
        // steps: pre-migration the chip was a fixed px box (Chip.js `height:
        // 32`, small `:24`) while rem steps track the display-size root font
        // (30/32/36px measured at small/medium/large). Pinned for parity —
        // display-size-aware chip geometry is a Phase-2 decision (design
        // review round 1, D7).
        //
        // Small carries the same 12px/500 label type as medium: the app's
        // `MuiChip` root override (theme.ts) pinned BOTH sizes, so a small
        // label must not inherit the ambient context size (design review
        // round 1, D1).
        medium: 'h-[32px] text-[0.75rem] font-medium',
        small: 'h-[24px] text-[0.75rem] font-medium',
      },
      rounded: {
        true: 'rounded-2xl',
        false: 'rounded-lg',
      },
      disabled: {
        /*
         * No `pointer-events-none`: the disabled cursor has to be reachable
         * for the chip to say `not-allowed`, and swallowing the click is what
         * a disabled chip should do anyway (it fires no handler either way).
         */
        true: 'opacity-[var(--minerva-palette-action-disabledOpacity)]',
        false: null,
      },
      focusable: {
        // WCAG 2.4.7 (focus visible): any chip that can receive keyboard focus
        // must SHOW it. This is keyed on `focusable` rather than `interactive`
        // because a chip that honours a caller-supplied `tabIndex` is
        // focusable without being interactive (e.g. the open-review
        // Unknown-status chip: its tooltip opens on focus). `outline-none` on
        // the root suppresses the UA ring, so without this a keyboard user tabs
        // to such a chip and sees nothing.
        //
        // The full outline shorthand (not outline-2/outline-[color]) on purpose:
        // `outline-none` sets the shared `--tw-outline-style: none` var, which
        // a bare `outline-2` would inherit, leaving focus ringless.
        //
        // The dark-scheme re-ink (ring = text.primary over an accent fill)
        // cannot sit in @layer components — utilities beat that layer
        // wholesale, which made the first version dead CSS (code review round
        // 1, R1-2). It lives in `src/tailwind.css`'s @layer utilities, the
        // SAME layer as this utility, where its extra scheme/variant
        // attributes win.
        true: 'focus-visible:[outline:2px_solid_var(--color-ring)] focus-visible:outline-offset-2',
        false: null,
      },
    },
    defaultVariants: {
      size: 'medium',
      rounded: false,
      disabled: false,
      focusable: false,
    },
  }
);

const labelClasses =
  'MuiChip-label min-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-[calc(var(--minerva-spacing)*1)] py-[calc(var(--minerva-spacing)*0.5)]';

/**
 * The chip's pointer affordance, by state — one expression, evaluated in the
 * component so the order can never decide it.
 *
 * WHY NOT THE BASE-LAYER CONTRACT (see `src/tailwind.css`). That rule matches
 * on the rendered element: `[role="button"]:not([aria-disabled="true"])` gives
 * a live chip its hand cursor, but an INTERACTIVE and DISABLED chip still
 * carries `role="button"` with no `aria-disabled`, so the contract cannot tell
 * it apart from a live one. And where the contract COULD tell (a chip carrying
 * `aria-disabled="true"`), this class still wins: it is a utility, and
 * utilities outrank `@layer base`. That is why `ariaDisabled` is one of the two
 * inputs rather than being left to the contract — the one production
 * `aria-disabled` chip today drops its `onClick` at the same moment it sets the
 * attribute, so a hand there would have been a lie nothing enforced (design
 * round 1, D1).
 *
 * Three states, three answers: a chip that is neither clickable nor disabled is
 * genuinely inert and stays on `default` (the MUI affordance worth keeping — it
 * is the cursor that says "this is a label, not a button").
 */
const cursorClassFor = ({
  ariaDisabled,
  disabled,
  interactive,
}: {
  ariaDisabled: boolean;
  disabled: boolean;
  interactive: boolean;
}) => {
  if (disabled || ariaDisabled) {
    return 'cursor-not-allowed';
  }
  return interactive ? 'cursor-pointer' : 'cursor-default';
};

/**
 * The MUI colour NAME for a tone, so the inert hook classes MUI's own DOM
 * carried (`MuiChip-colorDefault`, `MuiChip-filledPrimary`, …) keep matching.
 * The wrapper maps MUI's `default` to the `neutral` tone; everything else is
 * identity.
 */
const MUI_COLOR_FOR_TONE: Record<ChipTone, string> = {
  neutral: 'default',
  primary: 'primary',
  secondary: 'secondary',
  error: 'error',
  warning: 'warning',
  success: 'success',
  info: 'info',
};

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * The generated class set MUI's `useUtilityClasses` emits for the chip's
 * slots (Chip.js, 6.5.0), including the classes MUI's ButtonBase added to the
 * root whenever it rendered one (`clickable || onDelete` there, i.e.
 * `interactive || onDelete` here) and the `iconColor` class on the icon span.
 * These are INERT selector hooks — consumer tests and any surviving userland
 * CSS match them, and MUI's own stylesheet only does if a raw MUI Chip
 * renders elsewhere, in which case @layer components/utilities still outrank
 * its @layer-mui rules. Do NOT rename; keep in sync with MUI if the chip's
 * own vocabulary ever changes.
 */
const muiHookClasses = ({
  disabled,
  interactive,
  onDelete,
  size,
  tone,
  variant,
}: {
  disabled: boolean;
  interactive: boolean;
  onDelete?: (event: React.SyntheticEvent) => void;
  size: ChipSize;
  tone: ChipTone;
  variant: ChipVariant;
}) => {
  const muiColor = MUI_COLOR_FOR_TONE[tone];
  const muiColorCap = capitalize(muiColor);
  const sizeCap = capitalize(size);
  return {
    deleteIcon: [
      `MuiChip-deleteIcon${sizeCap}`,
      `MuiChip-deleteIconColor${muiColorCap}`,
      `MuiChip-deleteIcon${capitalize(variant)}Color${muiColorCap}`,
    ],
    icon: [`MuiChip-icon${sizeCap}`, `MuiChip-iconColor${muiColorCap}`],
    label: [`MuiChip-label${sizeCap}`],
    root: [
      `MuiChip-${variant}`,
      disabled && 'MuiChip-disabled',
      `MuiChip-size${sizeCap}`,
      `MuiChip-color${muiColorCap}`,
      interactive && 'MuiChip-clickable',
      interactive && `MuiChip-clickableColor${muiColorCap}`,
      onDelete && 'MuiChip-deletable',
      onDelete && `MuiChip-deletableColor${muiColorCap}`,
      `MuiChip-${variant}${muiColorCap}`,
      // MUI renders the root as ButtonBase whenever the chip is clickable or
      // deletable; its own generated classes ride along on the same element
      // (ButtonBase.js, 6.5.0). Inert hooks, same as the rest of the set.
      (interactive || onDelete) && 'MuiButtonBase-root',
      (interactive || onDelete) && disabled && 'MuiButtonBase-disabled',
    ],
  };
};

const iconClasses = (size: ChipSize) =>
  size === 'small' ? 'ml-[4px] mr-[-4px] text-[18px]' : 'ml-[5px] mr-[-6px]';

const deleteButtonClasses = (size: ChipSize) =>
  size === 'small'
    ? 'mr-[4px] ml-[-4px] text-[16px]'
    : 'ml-[-6px] mr-[5px] text-[22px]';

export const Chip = React.forwardRef<HTMLElement, UiChipProps>(
  (
    {
      className,
      component,
      deleteIcon,
      deleteLabel = 'Delete',
      disabled = false,
      icon,
      interactive = false,
      label,
      onClick,
      onDelete,
      rounded,
      size = 'medium',
      slotProps,
      // Captured explicitly so an EXPLICIT caller tabIndex is honoured below
      // instead of being silently dropped by the later fallback key. Before
      // this the later `tabIndex:` key always won, so a non-interactive caller
      // that asked to be focusable (a chip whose tooltip must open on focus for
      // keyboard/AT users) was not. Every other caller omits it, so this is
      // `undefined` for them and behaviour is unchanged.
      tabIndex,
      tone = 'neutral',
      variant = 'outlined',
      ...rest
    },
    ref
  ) => {
    /**
     * Two explicit branches instead of one polymorphic element: a `span` root
     * must not receive `disabled`/`type`, and the two intrinsics have
     * different ref types that a single `ElementType` alias cannot express
     * (the ref is HTMLSpanElement here, HTMLButtonElement there).
     */

    /**
     * The chip has TWO ways of saying "disabled", and they are not the same
     * thing:
     *
     * - `disabled` is the prop, and on the `button` root it becomes the real
     *   attribute (the browser then suppresses the click itself);
     * - `aria-disabled` is how this app marks a control that stays
     *   announced-but-unavailable — MUI's ButtonBase keeps such a control
     *   focusable on purpose, so the attribute and not the prop is what the
     *   production chip carries (`CustomerRiskRatingConfigView`, read-only
     *   tenants).
     *
     * Both must reach the AFFORDANCE and the DOM state, so `isDisabled` (the
     * union) drives `cursorClassFor` and `data-disabled`; only the prop drives
     * the handler, which is MUI's behaviour (see the note on `onClick`).
     */
    const ariaDisabled =
      rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';
    const isDisabled = disabled || ariaDisabled;

    /**
     * MUI parity (Chip.js handleKeyDown/handleKeyUp): Backspace/Delete on the
     * chip itself deletes, but only for chips that own keyboard focus
     * semantics (here: `interactive`); for non-interactive deletable chips the
     * delete affordance is the real delete button, which is keyboard-operable
     * in its own right. Enter/Space trigger the click for interactive chips.
     */
    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.currentTarget !== event.target) {
        return;
      }
      if (interactive && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        event.currentTarget.click();
      } else if (
        interactive &&
        onDelete &&
        (event.key === 'Backspace' || event.key === 'Delete')
      ) {
        // preventDefault is what stops some browsers navigating back; the
        // delete itself lands on keyUp, exactly like MUI's Chip.
        event.preventDefault();
      }
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
      if (
        interactive &&
        onDelete &&
        event.currentTarget === event.target &&
        (event.key === 'Backspace' || event.key === 'Delete')
      ) {
        onDelete(event);
      }
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      // Stop the click bubbling into the chip's own onClick, like MUI's
      // handleDeleteIconClick.
      event.stopPropagation();
      onDelete?.(event);
    };

    const hookClasses = muiHookClasses({
      disabled,
      interactive,
      onDelete,
      size,
      tone,
      variant,
    });

    const rootClassName = cn(
      'minerva-chip MuiChip-root',
      chipRootVariants({
        disabled,
        // Focusable = interactive, or a non-interactive chip the caller made
        // focusable via an explicit tabIndex. `!== undefined` is deliberate: a
        // caller passing `tabIndex={undefined}` is NOT claiming focusability,
        // matching the tabIndex fallback's behaviour below.
        focusable: interactive || tabIndex !== undefined,
        rounded,
        size,
      }),
      cursorClassFor({ ariaDisabled, disabled, interactive }),
      ...hookClasses.root.filter((hook): hook is string => Boolean(hook)),
      className
    );

    const rootChildren = (
      <>
        {icon ? (
          <span
            data-slot="chip-icon"
            className={cn(
              'MuiChip-icon',
              ...hookClasses.icon,
              iconClasses(size),
              slotProps?.icon?.className
            )}
            style={slotProps?.icon?.style}
          >
            {icon}
          </span>
        ) : null}

        <span
          data-slot="chip-label"
          className={cn(
            labelClasses,
            ...hookClasses.label,
            slotProps?.label?.className
          )}
          style={slotProps?.label?.style}
        >
          {label}
        </span>

        {onDelete ? (
          <button
            aria-label={deleteLabel}
            data-slot="chip-delete"
            className={cn(
              // The cursor is the base-layer contract's (hand when it can be
              // pressed, `not-allowed` when the chip is disabled). An explicit
              // `cursor-pointer` here was a lie on a disabled chip's delete icon.
              'MuiChip-deleteIcon',
              ...hookClasses.deleteIcon,
              deleteButtonClasses(size),
              slotProps?.delete?.className
            )}
            disabled={disabled}
            onClick={handleDeleteClick}
            style={slotProps?.delete?.style}
            type="button"
          >
            {deleteIcon ?? <DefaultDeleteIcon />}
          </button>
        ) : null}
      </>
    );

    const commonProps = {
      ...rest,
      'data-chip-interactive': interactive || undefined,
      'data-chip-tone': tone,
      'data-chip-variant': variant,
      /*
       * THE STATE, ON THE ELEMENT. A `span` root never gets a real `disabled`
       * attribute (only the `button` component does, and the browser suppresses
       * that one's click), so without this a disabled chip is indistinguishable
       * from a live one to anything reading the DOM — which is exactly how the
       * consistency instrument missed it: the probe measured
       * `cursor: not-allowed` on a chip it recorded as `disabled: false`, so the
       * check asserted nothing about it (code review round 1, F2 / design D6).
       * Its sibling atoms already declare the state this way (`ui/checkbox`,
       * `ui/radio`, `ui/switch`, `ui/choice-group`), and the chip's own hover
       * recipes key off it so an interactive+disabled chip cannot tint (design
       * round 1, D2).
       *
       * `aria-disabled` counts, because for this control it IS the disabled
       * state when set (the caller keeps the chip operable-looking on purpose).
       */
      'data-disabled': isDisabled || undefined,
      /*
       * A DISABLED chip is INERT, and the handler is what has to say so now.
       *
       * The `pointer-events: none` this state used to carry did that job by
       * keeping the click off the element altogether; it is gone because a
       * `not-allowed` cursor has to be reachable, which requires the pointer to
       * land here. Without this the removal would have let a disabled span chip
       * invoke its own `onClick` — a disabled control performing an action.
       * MUI's ButtonBase guards the same way in `handleClick`, and this is the
       * parity it was keeping by other means.
       *
       * KEYED ON THE PROP, NOT ON `aria-disabled`, and that split is deliberate:
       * MUI's ButtonBase blocks on `disabled` alone, and a caller that sets
       * `aria-disabled` owns what its own click should do (the one production
       * site drops its `onClick` at the same moment it sets the attribute, so
       * this is parity rather than a change). The AFFORDANCE, unlike the
       * handler, does read `aria-disabled` (see `cursorClassFor`) — a control
       * that tells assistive tech it is disabled must not promise a hand.
       */
      onClick: disabled ? undefined : onClick,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      // The button engine owns interactive semantics; inert chips retain
      // caller-owned image/status roles and explicit focus affordances.
      //
      // `role`: a non-interactive chip keeps the caller's own role (an inert
      // `role="img"` PEP indicator), so the atom's rule must fall back to
      // `rest.role` rather than blanking it.
      //
      // `tabIndex`: the caller's explicit value is captured above (destructured
      // out of `rest`), so the non-interactive fallback reads the captured
      // `tabIndex`, never `rest.tabIndex` — that is now always undefined, which
      // would silently drop the prop again.
      role: interactive ? 'button' : rest.role,
      tabIndex: interactive && !disabled ? 0 : tabIndex,
    };

    if (component === 'button') {
      return (
        <button
          {...commonProps}
          className={rootClassName}
          disabled={disabled}
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
        >
          {rootChildren}
        </button>
      );
    }

    return (
      <span
        {...commonProps}
        className={rootClassName}
        ref={ref as React.Ref<HTMLSpanElement>}
      >
        {rootChildren}
      </span>
    );
  }
);
Chip.displayName = 'Chip';
